import React, { useEffect } from 'react';
import { Link, Outlet, useNavigate, useLocation } from 'react-router-dom';
import { 
  LayoutDashboard, Plug, CreditCard, Webhook, LogOut, 
  ChevronRight, Shield, Crown, FileText, Globe, Flag
} from 'lucide-react';
import { useAdmin } from '../../context/AdminContext';
import { Button } from '../../components/ui/button';
import { Avatar, AvatarFallback } from '../../components/ui/avatar';
import { Loader2 } from 'lucide-react';

const NAV_ITEMS = [
  { path: '/admin', label: 'Dashboard', icon: LayoutDashboard, exact: true },
  { path: '/admin/subscriptions', label: 'Subscriptions', icon: Crown },
  { path: '/admin/content', label: 'Content / Listings', icon: FileText },
  { path: '/admin/agency-review', label: 'Agency Review', icon: Flag },
  { path: '/admin/metadata', label: 'SEO / Metadata', icon: Globe },
  { path: '/admin/integrations', label: 'Integrations', icon: Plug },
  { path: '/admin/payments', label: 'Payments (Stripe)', icon: CreditCard },
  { path: '/admin/webhooks', label: 'Webhooks / Status', icon: Webhook },
];

const AdminLayout = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { admin, loading, isAuthenticated, logout } = useAdmin();

  useEffect(() => {
    if (!loading && !isAuthenticated) {
      navigate('/admin/login');
    }
  }, [loading, isAuthenticated, navigate]);

  const handleLogout = async () => {
    await logout();
    navigate('/admin/login');
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-900 flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-trust-blue" />
      </div>
    );
  }

  if (!isAuthenticated) {
    return null;
  }

  return (
    <div className="min-h-screen bg-slate-900 flex" data-testid="admin-layout">
      {/* Sidebar */}
      <aside className="w-64 bg-slate-800 border-r border-slate-700 flex flex-col">
        {/* Logo */}
        <div className="p-6 border-b border-slate-700">
          <Link to="/" className="flex items-center gap-3">
            <img 
              src="/staypairhomes-logo.png" 
              alt="Stay Pair Homes" 
              className="h-10 w-auto object-contain"
            />
            <div>
              <div className="flex items-center gap-1 text-xs text-slate-400">
                <Shield className="w-3 h-3" />
                Admin Panel
              </div>
            </div>
          </Link>
        </div>

        {/* Navigation */}
        <nav className="flex-1 p-4 space-y-1">
          {NAV_ITEMS.map((item) => {
            const isActive = item.exact 
              ? location.pathname === item.path 
              : location.pathname.startsWith(item.path);
            
            return (
              <Link
                key={item.path}
                to={item.path}
                className={`flex items-center gap-3 px-4 py-3 rounded-xl transition-colors ${
                  isActive 
                    ? 'bg-trust-blue text-white' 
                    : 'text-slate-400 hover:text-white hover:bg-slate-700'
                }`}
                data-testid={`nav-${item.label.toLowerCase().replace(/[^a-z]/g, '-')}`}
              >
                <item.icon className="w-5 h-5" />
                <span className="font-medium">{item.label}</span>
                {isActive && <ChevronRight className="w-4 h-4 ml-auto" />}
              </Link>
            );
          })}
        </nav>

        {/* User Section */}
        <div className="p-4 border-t border-slate-700">
          <div className="flex items-center gap-3 mb-4">
            <Avatar className="w-10 h-10">
              <AvatarFallback className="bg-trust-blue text-white">
                {admin?.name?.charAt(0) || 'A'}
              </AvatarFallback>
            </Avatar>
            <div className="flex-1 min-w-0">
              <p className="text-white font-medium truncate">{admin?.name}</p>
              <p className="text-slate-400 text-sm truncate">{admin?.email}</p>
            </div>
          </div>
          <Button
            variant="outline"
            className="w-full border-slate-600 text-slate-300 hover:text-white hover:bg-slate-700"
            onClick={handleLogout}
            data-testid="admin-logout"
          >
            <LogOut className="w-4 h-4 mr-2" />
            Log out
          </Button>
        </div>
      </aside>

      {/* Main Content */}
      <main className="flex-1 overflow-auto">
        <Outlet />
      </main>
    </div>
  );
};

export default AdminLayout;
